import { Box, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { Icons } from "../assets/icon";

interface ImageUploadProps {
  onFileSelect: (files: FileList | null) => void;
  name?: string;
  accept?: string;
  multiple?: boolean;
  label?: string;
  subLabel?: string;
  maxFiles?: number;
  layout?: "row" | "column";
  browseButtonLabel?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onFileSelect,
  accept = "image/*",
  name,
  multiple = false,
  label = "Drag and drop image to upload",
  subLabel = "SVG, PNG, JPG or GIF (max. 800x400px)",
  maxFiles = 5,
  layout = "column",
  browseButtonLabel = "Browse Image",
}) => {
  const [error, setError] = useState<string | null>(null);

  const handleFiles = (fileList: FileList | null) => {
    if (fileList) {
      const newFiles = Array.from(fileList);
      if (newFiles.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }
      onFileSelect(fileList);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  return (
    <Stack direction="row" gap={2}>
      <Box
        sx={{
          border: "2px dashed var(--primary-300)",
          borderRadius: 3,
          p: layout === "row" ? 2 : 4,
          textAlign: "center",
          backgroundColor: "var(--neutral-50)",
          cursor: "pointer",
          flex: 1,
          "&:hover": { backgroundColor: "var(--neutral-100)" },
          width: "100%",
          display: "flex",
          flexDirection: layout === "row" ? "row" : "column",
          alignItems: layout === "row" ? "center" : "stretch",
          justifyContent: layout === "row" ? "space-between" : "center",
          gap: layout === "row" ? 4 : 2,
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <Stack
          direction="column"
          gap={"var(--spacing-2x)"}
          sx={{
            flex: layout === "row" ? 1 : "none",
            alignItems: layout === "row" ? "flex-start" : "center",
            textAlign: layout === "row" ? "left" : "center",
          }}
        >
          <Typography variant="body1" sx={{ font: "var(--semibold-md)" }}>
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{ font: "var(--body-sm)", color: "var(--neutral-500)" }}
          >
            {subLabel}
          </Typography>
        </Stack>
        <label htmlFor={name || "file-upload"}>
          <input
            accept={accept}
            type="file"
            id={name || "file-upload"}
            hidden
            multiple={multiple}
            onChange={handleFileChange}
          />
          <div
            style={{
              borderRadius: "12px",
              color: "var(--neutral-900)",
              border: "1px solid var(--neutral-200)",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              width: "fit-content",
              margin: layout === "row" ? 0 : "0 auto",
            }}
          >
            <Icons.ImageUserPlus height={20} width={20} />
            <Typography variant="body2" sx={{ font: "var(--semibold-sm)" }}>
              {browseButtonLabel}
            </Typography>
          </div>
        </label>
      </Box>
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Stack>
  );
};

export default ImageUpload;
